// /purchase-orders/daily — Edit the single "DAILY PO" list.
//
// The owner sets up one reusable order list (items + quantities + supplier +
// location). The "Generate DAILY PO" button on the Purchase Order page turns it
// into a placed (ORDERED) purchase order in one click. Restaurant tenants pick
// ingredients; retail tenants pick products — same rule as the PO create page.

import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { PageContainer, ErrorBoundary, Input, Select, Button, Skeleton, useToast } from '@bbpos/ui';
import { purchaseOrdersApi } from '@/features/purchase-orders/api';
import type { SaveDailyPoPayload } from '@/features/purchase-orders/api';
import { locationService } from '@/services/location.service';
import { productService } from '@/services/product.service';
import { ingredientService } from '@/services/ingredient.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { ApiError } from '@/lib/api-client';

const UNIT_OPTIONS = [
  { value: 'PCS', label: 'Pieces' }, { value: 'KG', label: 'Kilograms' }, { value: 'G', label: 'Grams' },
  { value: 'L', label: 'Litres' }, { value: 'ML', label: 'Millilitres' }, { value: 'DOZEN', label: 'Dozen' },
  { value: 'BOX', label: 'Box' }, { value: 'CARTON', label: 'Carton' },
];

interface Row { key: string; item_id: string; name: string; quantity: string; unit: string; unit_cost: string }

let keySeq = 0;
const newKey = () => `row-${keySeq++}`;

function DailyPoTemplateContent() {
  const dial = useTenantDial();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  const businessType = useAuthStore((s) => s.profile?.tenant?.business_type);
  const isRestaurant = businessType === 'RESTAURANT';
  const noun = isRestaurant ? 'ingredient' : 'product';

  const [locationId, setLocationId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [supplierEmail, setSupplierEmail] = useState('');
  const [rows, setRows] = useState<Row[]>([{ key: newKey(), item_id: '', name: '', quantity: '', unit: 'PCS', unit_cost: '' }]);

  const { data: template, isLoading } = useQuery({ queryKey: ['daily-po-template'], queryFn: () => purchaseOrdersApi.getDailyTemplate() });
  const { data: locationsData } = useQuery({ queryKey: ['locations-list'], queryFn: () => locationService.list({ limit: 100, is_active: 'true' }), staleTime: 60_000 });
  const { data: productsData } = useQuery({ queryKey: ['daily-po-products'], queryFn: () => productService.list({ limit: 200, has_variants: 'false' }), enabled: !isRestaurant, staleTime: 30_000 });
  const { data: ingredientsData } = useQuery({ queryKey: ['daily-po-ingredients'], queryFn: () => ingredientService.listIngredients({ is_active: 'true', limit: 200 }), enabled: isRestaurant, staleTime: 30_000 });

  // Seed the form once the saved template arrives.
  useEffect(() => {
    if (!template) return;
    setLocationId(template.location_id ?? '');
    setSupplierName(template.supplier_name ?? '');
    setSupplierPhone(template.supplier_phone ?? '');
    setSupplierEmail(template.supplier_email ?? '');
    if (template.items.length > 0) {
      setRows(template.items.map((i) => ({
        key: newKey(),
        item_id: (isRestaurant ? i.ingredient_id : i.product_id) ?? '',
        name: i.name ?? '',
        quantity: String(i.quantity),
        unit: i.unit,
        unit_cost: (i.unit_cost / subunit).toFixed(dp),
      })));
    }
  }, [template, isRestaurant]);

  const catalog = useMemo(() => {
    if (isRestaurant) return (ingredientsData?.data ?? []).map((g) => ({ id: g.id, name: g.name, cost: g.cost }));
    return (productsData?.data ?? []).map((p) => ({ id: p.id, name: `${p.name} (${p.sku})`, cost: p.cost }));
  }, [isRestaurant, ingredientsData, productsData]);

  const locationOptions = useMemo(() => (locationsData?.data ?? []).map((l) => ({ value: l.id, label: l.name })), [locationsData]);
  const itemOptions = useMemo(() => catalog.map((c) => ({ value: c.id, label: c.name })), [catalog]);

  function updateRow(key: string, field: keyof Row, value: string) {
    setRows((prev) => prev.map((r) => {
      if (r.key !== key) return r;
      const u = { ...r, [field]: value };
      if (field === 'item_id') {
        const picked = catalog.find((c) => c.id === value);
        if (picked) { u.name = picked.name; if (!r.unit_cost && picked.cost) u.unit_cost = (picked.cost / subunit).toFixed(dp); }
      }
      return u;
    }));
  }

  const save = useMutation({
    mutationFn: () => {
      const items = rows
        .filter((r) => r.item_id && parseFloat(r.quantity) > 0)
        .map((r) => ({
          ...(isRestaurant ? { ingredient_id: r.item_id } : { product_id: r.item_id }),
          quantity: parseFloat(r.quantity),
          unit: r.unit,
          unit_cost: Math.round((parseFloat(r.unit_cost) || 0) * subunit),
        }));
      const payload: SaveDailyPoPayload = {
        location_id: locationId || undefined,
        supplier_name: supplierName.trim() || undefined,
        supplier_phone: supplierPhone || undefined,
        supplier_email: supplierEmail || undefined,
        items,
      };
      return purchaseOrdersApi.saveDailyTemplate(payload);
    },
    onSuccess: () => { toast({ title: 'DAILY PO list saved', variant: 'success' }); navigate('/purchase-orders'); },
    onError: (err: Error) => toast({ title: err instanceof ApiError ? err.message : 'Could not save', variant: 'error' }),
  });

  const validItemCount = rows.filter((r) => r.item_id && parseFloat(r.quantity) > 0).length;
  const canSave = !!locationId && !!supplierName.trim() && validItemCount > 0 && !save.isPending;

  if (isLoading) return <Skeleton variant="rect" height={360} className="rounded-2xl" />;

  return (
    <div className="animate-fade-in space-y-6">
      <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-white shadow-lg">
        <h1 className="text-2xl font-bold">Edit DAILY PO list</h1>
        <p className="mt-1 text-sm text-white/85">Set the {noun}s, quantities and supplier you order every day. Then use “Generate DAILY PO” to place it in one click.</p>
      </div>

      <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Receiving Location <span className="text-danger-500">*</span></label>
            <Select options={locationOptions} value={locationId} onChange={(v) => setLocationId(typeof v === 'string' ? v : '')} placeholder="Select location" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Supplier Name <span className="text-danger-500">*</span></label>
            <Input value={supplierName} onChange={setSupplierName} placeholder="e.g. Fresh Farms Wholesale" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Supplier Phone</label>
            <Input variant="phone" defaultDial={dial} value={supplierPhone} onChange={setSupplierPhone} placeholder="98765 43210" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Supplier Email</label>
            <Input type="email" value={supplierEmail} onChange={setSupplierEmail} placeholder="orders@supplier.com" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Items ({isRestaurant ? 'Ingredients' : 'Products'})</h3>
          </div>
          <div className="overflow-visible">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-700 text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  <th className="text-left py-2 pr-3 w-2/5">{isRestaurant ? 'Ingredient' : 'Product'}</th>
                  <th className="text-left py-2 pr-3 w-24">Quantity</th>
                  <th className="text-left py-2 pr-3 w-32">Unit</th>
                  <th className="text-left py-2 pr-3 w-32">Unit Cost ({symbol})</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0">
                    <td className="py-2 pr-3">
                      <Select
                        options={[...(r.item_id ? [{ value: r.item_id, label: r.name || r.item_id }] : []), ...itemOptions]}
                        value={r.item_id}
                        onChange={(v) => updateRow(r.key, 'item_id', typeof v === 'string' ? v : '')}
                        placeholder={`Select ${noun}`}
                      />
                    </td>
                    <td className="py-2 pr-3"><Input type="number" value={r.quantity} onChange={(v) => updateRow(r.key, 'quantity', v)} placeholder="0" /></td>
                    <td className="py-2 pr-3"><Select options={UNIT_OPTIONS} value={r.unit} onChange={(v) => updateRow(r.key, 'unit', typeof v === 'string' ? v : 'PCS')} /></td>
                    <td className="py-2 pr-3"><Input type="number" value={r.unit_cost} onChange={(v) => updateRow(r.key, 'unit_cost', v)} placeholder="0.00" /></td>
                    <td className="py-2 text-right">
                      <button type="button" aria-label="Remove row" onClick={() => setRows((prev) => (prev.length > 1 ? prev.filter((x) => x.key !== r.key) : prev))} className="text-danger-500 hover:text-danger-700">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="secondary" className="mt-3" onClick={() => setRows((prev) => [...prev, { key: newKey(), item_id: '', name: '', quantity: '', unit: 'PCS', unit_cost: '' }])}>
            + Add {noun}
          </Button>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => navigate('/purchase-orders')}>Cancel</Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={!canSave} loading={save.isPending}>Save DAILY PO list</Button>
        </div>
      </div>
    </div>
  );
}

export function DailyPoTemplatePage() {
  return (
    <PageContainer title="DAILY PO" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Purchase Order', href: '/purchase-orders' }, { label: 'DAILY PO' }]}>
      <ErrorBoundary resetKey="daily-po-template">
        <DailyPoTemplateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
